import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { AuthProvider, useAuth } from './lib/auth'
import Login from './pages/Login'
import AllTools from './pages/AllTools'
import Vierkantjes from './pages/Vierkantjes'
// Lazy: the page inlines Max Morel's 60 drawings (~270 KB), which no other page needs.
const Dwarsdoorsnede = React.lazy(() => import('./pages/Dwarsdoorsnede'))
// ODIN Explorer DISABLED 2026-07-01 (tool audit): taken offline pending an OGC-native rebuild
// (read live data via /oapi odin-query; drop the /api/odin/* legacy fallback). Page kept at
// pages/ODIN.tsx — re-add this import + the /odin route + the registry entry to republish.
import AccessibilityMaps from './pages/AccessibilityMaps'
import InnoFlashes from './pages/InnoFlashes'
// ManusGPT + Talentscout archived 2026-05-06 to ~/Documents/Projects/sb-experimental-tools.
// Both were Ollama-backed LLM experiments outside the data-tools mission.
import SelfServiceToolsPage from './pages/SelfServiceTools'
// /viewer is owned by the sb-viewer SvelteKit app (separate container).
// Traefik PathPrefix(/viewer) priority 100 routes it there. Auth-gated via
// forwardauth middleware → /api/auth/gate on this backend. Do not add a
// React route at /viewer — it would be unreachable.
import DataTeamServicesPage from './pages/DataTeamServices'
import MapsLibrary from './pages/MapsLibrary'
import Projecten from './pages/Projecten'
// ArturoSB archived 2026-07-01 (tool audit) — unused experiment; page kept at pages/ArturoSB.tsx, not routed.
import Spinnen from './pages/Spinnen'
import TravelTime from './pages/TravelTime'
// NRM is RETIRED from this SPA (2026-08-19): the Gen-2 sb-nrm-toolkit owns
// tools.sb.nl/nrm at the Traefik layer. These routes hard-exit the SPA so
// client-side navigation can never resurrect the Gen-1 component.
const ExitToNrm = () => {
  window.location.replace('/nrm')
  return null
}
import DocumentationLayout from './pages/Documentation'
import { OverviewPage } from './pages/documentation/pages/OverviewPage'
import { ExternalSourcesPage } from './pages/documentation/pages/ExternalSourcesPage'
import { MethodologiesPage } from './pages/documentation/pages/MethodologiesPage'
import { WorkInProgressPage } from './pages/documentation/pages/WorkInProgressPage'
import { ProjectShowcasePage } from './pages/documentation/pages/ProjectShowcasePage'
import { MapHubPage } from './pages/documentation/pages/MapHubPage'

import './styles/globals.css'

// Create query client for data fetching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center sb-gradient-bg">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="sb-text-gradient text-xl font-semibold">
            Loading Studio Bereikbaar Platform...
          </div>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  return user ? <>{children}</> : <Navigate to="/login" replace />
}

// Main App component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-background text-foreground">
            {/* Skip navigation link for accessibility */}
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-sb-benzol-green focus:text-white focus:rounded-md focus:shadow-lg"
              style={{
                fontWeight: '500',
                fontSize: '14px',
              }}
            >
              Skip to main content
            </a>

            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />

              {/* Protected routes */}
              {/* NRM lives in sb-nrm-toolkit (Gen-2) — hard exit, no SPA render */}
              <Route path="/traffic" element={<ExitToNrm />} />
              <Route path="/nrm" element={<ExitToNrm />} />
              <Route path="/nrm/*" element={<ExitToNrm />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AllTools />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vierkantjes"
                element={
                  <ProtectedRoute>
                    <Vierkantjes />
                  </ProtectedRoute>
                }
              />

              {/* Dwarsdoorsnede Builder (engine: src/tools/dwarsdoorsnede). The old single-file build at
                  /tools/dwarsdoorsnede/index.html stays one release as a fallback; nothing links to it. */}
              <Route
                path="/dwarsdoorsnede"
                element={
                  <ProtectedRoute>
                    <React.Suspense fallback={<div className="min-h-screen bg-[#F4F7FC]" />}>
                      <Dwarsdoorsnede />
                    </React.Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/tools"
                element={
                  <ProtectedRoute>
                    <SelfServiceToolsPage />
                  </ProtectedRoute>
                }
              />

              {/* Kaarten & Bibliotheek landing: cards to /map (GeoLibre), the STAC catalog and /viewer.
                  Not /maps: the /map Traefik router (PathPrefix) would catch it before this SPA. */}
              <Route
                path="/kaarten"
                element={
                  <ProtectedRoute>
                    <MapsLibrary />
                  </ProtectedRoute>
                }
              />

              {/* Projecten: the intake app (/intake/*) framed under the sidebar. Not /projecten:
                  the Streamlit /project router (PathPrefix) would catch it before this SPA. */}
              <Route
                path="/alle-projecten"
                element={
                  <ProtectedRoute>
                    <Projecten />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/services"
                element={
                  <ProtectedRoute>
                    <DataTeamServicesPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/accessibility-maps"
                element={
                  <ProtectedRoute>
                    <AccessibilityMaps />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/spinnen"
                element={
                  <ProtectedRoute>
                    <Spinnen />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/traveltime"
                element={
                  <ProtectedRoute>
                    <TravelTime />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/innoflashes"
                element={
                  <ProtectedRoute>
                    <InnoFlashes />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/documentation/*"
                element={
                  <ProtectedRoute>
                    <DocumentationLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<OverviewPage />} />
                <Route path="external-sources" element={<ExternalSourcesPage />} />
                <Route path="methodologies" element={<MethodologiesPage />} />
                <Route path="showcase" element={<ProjectShowcasePage />} />
                <Route path="maphub" element={<MapHubPage />} />
                <Route path="work-in-progress" element={<WorkInProgressPage />} />
              </Route>

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 404 fallback */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center sb-gradient-bg">
                    <div className="text-center">
                      <h1 className="sb-text-gradient text-4xl font-bold mb-4">404</h1>
                      <p className="text-muted-foreground text-lg mb-6">
                        Page not found
                      </p>
                      <a
                        href="/dashboard"
                        className="sb-btn-primary inline-block"
                      >
                        Back to Dashboard
                      </a>
                    </div>
                  </div>
                }
              />
            </Routes>
          </div>
        </BrowserRouter>

        {/* React Query DevTools (development only) */}
        {import.meta.env.DEV && (
          <ReactQueryDevtools
            initialIsOpen={false}
          />
        )}
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
